/**
 * Client-side export of TipTap / ProseMirror JSON to PDF (jsPDF) and DOCX (docx).
 * Libraries are loaded on first use via dynamic import() to avoid bloating the initial bundle.
 *
 * Fidelity (v1): structure and common marks (bold, italic, underline, strike, code, links).
 * PDF uses plain typography for body text; tables are drawn as a grid with wrapped cell text.
 */

/** @param {string} base */
export function sanitizeDownloadFileBase(base) {
  const raw = String(base ?? 'document').trim() || 'document';
  return raw.replace(/[/\\?%*:|"<>]/g, '_').replace(/\s+/g, '_').slice(0, 120) || 'document';
}

/** @param {Blob} blob @param {string} filename */
export function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  queueMicrotask(() => URL.revokeObjectURL(url));
}

/** @param {import('@tiptap/core').JSONContent} node */
function extractPlainTextFromNode(node) {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (!node.content) return '';
  return node.content.map(extractPlainTextFromNode).join('');
}

/**
 * Flattens inline content into styled runs (bold/italic/strike/code), so PDF
 * body text can keep marks instead of always rendering plain text.
 *
 * @param {import('@tiptap/core').JSONContent} node
 * @returns {Array<{ text: string, bold?: boolean, italic?: boolean, strike?: boolean, code?: boolean, hardBreak?: boolean }>}
 */
function extractRunsFromNode(node) {
  /** @type {Array<{ text: string, bold?: boolean, italic?: boolean, strike?: boolean, code?: boolean, hardBreak?: boolean }>} */
  const runs = [];
  const walk = (n) => {
    if (!n) return;
    if (n.type === 'hardBreak') {
      runs.push({ text: '', hardBreak: true });
      return;
    }
    if (n.type === 'text') {
      const marks = n.marks || [];
      runs.push({
        text: n.text ?? '',
        bold: marks.some((m) => m.type === 'bold'),
        italic: marks.some((m) => m.type === 'italic'),
        strike: marks.some((m) => m.type === 'strike'),
        code: marks.some((m) => m.type === 'code'),
      });
      return;
    }
    if (n.content) n.content.forEach(walk);
  };
  walk(node);
  return runs;
}

/**
 * Word-wraps styled runs into lines that fit `maxWidth`, measuring each word
 * with the font/style it will actually be drawn with.
 *
 * @param {import('jspdf').jsPDF} pdf
 * @param {Array<{ text: string, bold?: boolean, italic?: boolean, strike?: boolean, code?: boolean, hardBreak?: boolean }>} runs
 * @param {number} maxWidth
 * @param {number} fontSize
 * @returns {Array<Array<{ text: string, bold?: boolean, italic?: boolean, strike?: boolean, code?: boolean, width: number }>>}
 */
function wrapRunsToLines(pdf, runs, maxWidth, fontSize) {
  const setStyleFont = (r) => {
    pdf.setFontSize(fontSize);
    if (r.code) {
      pdf.setFont('courier', r.bold ? 'bold' : 'normal');
    } else {
      const style = r.bold && r.italic ? 'bolditalic' : r.bold ? 'bold' : r.italic ? 'italic' : 'normal';
      pdf.setFont('helvetica', style);
    }
  };

  /** @type {Array<{ text: string, bold?: boolean, italic?: boolean, strike?: boolean, code?: boolean, width: number }>} */
  const words = [];
  for (const run of runs) {
    if (run.hardBreak) {
      words.push({ text: '\n', width: 0, hardBreak: true });
      continue;
    }
    const parts = (run.text || '').split(/(\s+)/).filter((p) => p !== '');
    for (const part of parts) {
      setStyleFont(run);
      words.push({
        text: part,
        bold: run.bold,
        italic: run.italic,
        strike: run.strike,
        code: run.code,
        width: pdf.getTextWidth(part),
      });
    }
  }

  const lines = [];
  let current = [];
  let currentWidth = 0;
  for (const word of words) {
    if (word.hardBreak) {
      lines.push(current);
      current = [];
      currentWidth = 0;
      continue;
    }
    const isSpace = /^\s+$/.test(word.text);
    if (currentWidth + word.width > maxWidth && current.length && !isSpace) {
      // Drop a trailing space-only token before wrapping.
      while (current.length && /^\s+$/.test(current[current.length - 1].text)) current.pop();
      lines.push(current);
      current = [];
      currentWidth = 0;
    }
    current.push(word);
    currentWidth += word.width;
  }
  if (current.length) {
    while (current.length && /^\s+$/.test(current[current.length - 1].text)) current.pop();
    lines.push(current);
  }
  return lines.length ? lines : [[]];
}

/**
 * Draws word-wrapped styled runs starting at (x, y); returns the Y after the
 * last line drawn.
 *
 * @param {import('jspdf').jsPDF} pdf
 * @param {Array<{ text: string, bold?: boolean, italic?: boolean, strike?: boolean, code?: boolean, hardBreak?: boolean }>} runs
 * @param {number} x
 * @param {number} startY
 * @param {number} maxWidth
 * @param {number} fontSize
 * @param {number} lineHeight
 * @param {(currentY: number, neededMm: number) => number} ensureSpace
 * @returns {number}
 */
function drawRuns(pdf, runs, x, startY, maxWidth, fontSize, lineHeight, ensureSpace) {
  const lines = wrapRunsToLines(pdf, runs, maxWidth, fontSize);
  let y = startY;
  for (const line of lines) {
    // `ensureSpace` may trigger a page break — always take its return value
    // as the authoritative Y, never keep advancing a stale local cursor
    // (a page break resets the cursor to the new page's top margin, and a
    // caller ignoring that draws every subsequent line off the sheet).
    y = ensureSpace(y, lineHeight);
    let cursorX = x;
    for (const word of line) {
      pdf.setFontSize(fontSize);
      if (word.code) {
        pdf.setFont('courier', word.bold ? 'bold' : 'normal');
        pdf.setFillColor(242, 242, 242);
        pdf.rect(cursorX, y - fontSize * 0.32, word.width, fontSize * 0.42, 'F');
      } else {
        const style =
          word.bold && word.italic ? 'bolditalic' : word.bold ? 'bold' : word.italic ? 'italic' : 'normal';
        pdf.setFont('helvetica', style);
      }
      pdf.setTextColor(0, 0, 0);
      pdf.text(word.text, cursorX, y);
      if (word.strike) {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.15);
        pdf.line(cursorX, y - fontSize * 0.11, cursorX + word.width, y - fontSize * 0.11);
      }
      cursorX += word.width;
    }
    y += lineHeight;
  }
  pdf.setFont('helvetica', 'normal');
  return y;
}

/**
 * @param {import('@tiptap/core').JSONContent} tableNode
 * @returns {Array<{ cells: string[], header: boolean }>}
 */
function extractPdfTableRows(tableNode) {
  /** @type {Array<{ cells: string[], header: boolean }>} */
  const rows = [];
  for (const row of tableNode.content || []) {
    if (row.type !== 'tableRow') continue;
    const cells = [];
    let allHeader = true;
    for (const cell of row.content || []) {
      if (cell.type !== 'tableCell' && cell.type !== 'tableHeader') continue;
      if (cell.type === 'tableCell') allHeader = false;
      cells.push(extractPlainTextFromNode(cell).replace(/\s+/g, ' ').trim());
    }
    if (cells.length) {
      rows.push({ cells, header: Boolean(allHeader) });
    }
  }
  return rows;
}

/**
 * Draw a simple grid table (mm). Header rows use bold text + light fill.
 *
 * @param {import('jspdf').jsPDF} pdf
 * @param {Array<{ cells: string[], header: boolean }>} rows
 * @param {number} xStart
 * @param {number} usableWidth
 * @param {number} startY
 * @param {number} margin
 * @returns {number} Y after table
 */
function renderPdfTable(pdf, rows, xStart, usableWidth, startY, margin) {
  const pageBottom = 287;
  if (!rows.length) return startY;

  let y = startY;
  const colCount = Math.max(...rows.map((r) => r.cells.length), 1);
  const colW = usableWidth / colCount;
  const pad = 1.5;
  const lineHeight = 3.55;

  for (const row of rows) {
    const cells = [...row.cells];
    while (cells.length < colCount) cells.push('');

    pdf.setFontSize(9);
    pdf.setFont('helvetica', row.header ? 'bold' : 'normal');

    const wrapped = cells.map((t) =>
      pdf.splitTextToSize(String(t || ' '), Math.max(colW - pad * 2, 5))
    );
    const maxLines = Math.max(1, ...wrapped.map((w) => w.length));
    const rowHeight = maxLines * lineHeight + pad * 2;

    if (y + rowHeight > pageBottom) {
      pdf.addPage();
      y = margin;
    }

    let x = xStart;
    for (let c = 0; c < colCount; c += 1) {
      const lines = wrapped[c];
      if (row.header) {
        pdf.setFillColor(242, 242, 242);
        pdf.rect(x, y, colW, rowHeight, 'F');
      }
      pdf.setDrawColor(190, 190, 190);
      pdf.setLineWidth(0.15);
      pdf.rect(x, y, colW, rowHeight, 'S');
      pdf.setTextColor(30, 30, 30);
      pdf.text(lines, x + pad, y + pad + lineHeight * 0.85);
      x += colW;
    }
    y += rowHeight;
  }

  return y;
}

/**
 * @param {import('@tiptap/core').JSONContent} docJson
 * @returns {Array<{ kind: string, text?: string, rows?: Array<{ cells: string[], header: boolean }> }>}
 */
export function flattenTipTapForPdf(docJson) {
  /** @type {Array<{ kind: string, text?: string, rows?: Array<{ cells: string[], header: boolean }> }>} */
  const out = [];
  if (!docJson || docJson.type !== 'doc' || !docJson.content) return out;

  const walkBlock = (node) => {
    if (!node) return;
    switch (node.type) {
      case 'paragraph':
        out.push({
          kind: 'paragraph',
          text: extractPlainTextFromNode(node).trimEnd(),
          runs: extractRunsFromNode(node),
        });
        break;
      case 'heading': {
        const level = node.attrs?.level ?? 1;
        out.push({
          kind: `h${level}`,
          text: extractPlainTextFromNode(node),
          runs: extractRunsFromNode(node),
        });
        break;
      }
      case 'blockquote':
        (node.content || []).forEach((child) => {
          walkBlock(child);
        });
        out.push({ kind: 'paragraph', text: '' });
        break;
      case 'bulletList':
      case 'orderedList': {
        let i = 1;
        for (const li of node.content || []) {
          if (li.type !== 'listItem') continue;
          const prefix = node.type === 'orderedList' ? `${i}. ` : '• ';
          for (const inner of li.content || []) {
            if (inner.type === 'paragraph') {
              out.push({
                kind: 'paragraph',
                text: `${prefix}${extractPlainTextFromNode(inner)}`,
                runs: [{ text: prefix }, ...extractRunsFromNode(inner)],
              });
            } else {
              walkBlock(inner);
            }
          }
          i += 1;
        }
        break;
      }
      case 'codeBlock':
        out.push({ kind: 'code', text: extractPlainTextFromNode(node) });
        break;
      case 'horizontalRule':
        out.push({ kind: 'hr', text: '—' });
        break;
      case 'table': {
        const rows = extractPdfTableRows(node);
        if (rows.length) {
          out.push({ kind: 'table', rows });
        }
        out.push({ kind: 'paragraph', text: '' });
        break;
      }
      default:
        if (node.content) node.content.forEach(walkBlock);
    }
  };

  docJson.content.forEach(walkBlock);
  return out;
}

/**
 * @param {import('@tiptap/core').Editor} editor
 * @param {string} fileNameBase
 */
export async function exportRichTextToPdf(editor, fileNameBase) {
  const jspdfMod = await import('jspdf');
  const JsPDF = jspdfMod.default;
  const json = editor.getJSON();
  const blocks = flattenTipTapForPdf(json);
  const safeBase = sanitizeDownloadFileBase(fileNameBase);

  const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const margin = 20;
  const pageWidth = 210 - margin * 2;
  let y = margin;

  // Pure: takes the caller's current Y, returns the Y to actually draw at
  // (unchanged, or the new page's top margin after a break). Every caller —
  // this loop and `drawRuns` — must overwrite its own cursor with the
  // return value instead of assuming its old Y survives a page break.
  const ensureSpace = (currentY, neededMm) => {
    if (currentY + neededMm > 287) {
      pdf.addPage();
      return margin;
    }
    return currentY;
  };

  for (const block of blocks) {
    if (block.kind === 'hr') {
      y = ensureSpace(y, 8);
      pdf.setDrawColor(180);
      pdf.line(margin, y + 2, 210 - margin, y + 2);
      y += 8;
      continue;
    }

    if (block.kind === 'code') {
      pdf.setFont('courier', 'normal');
      pdf.setFontSize(9);
      const lines = pdf.splitTextToSize(block.text || ' ', pageWidth);
      y = ensureSpace(y, lines.length * 4.5 + 4);
      pdf.text(lines, margin, y);
      y += lines.length * 4.5 + 4;
      pdf.setFont('helvetica', 'normal');
      continue;
    }

    if (block.kind === 'table' && block.rows?.length) {
      pdf.setDrawColor(190);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      y = renderPdfTable(pdf, block.rows, margin, pageWidth, y, margin);
      pdf.setFontSize(11);
      y += 4;
      continue;
    }

    if (block.kind.startsWith('h')) {
      const level = Number(block.kind.slice(1)) || 1;
      const size = Math.max(11, 18 - (level - 1) * 2);
      // Space above the heading so it doesn't sit flush against the
      // preceding paragraph — skip it when the heading opens the page.
      if (y > margin) {
        y = ensureSpace(y, 4);
        y += 4;
      }
      const lineHeight = size * 0.45;
      const runs = block.runs?.length ? block.runs : [{ text: block.text || ' ', bold: true }];
      const boldRuns = runs.map((r) => ({ ...r, bold: true }));
      y = drawRuns(pdf, boldRuns, margin, y, pageWidth, size, lineHeight, ensureSpace);
      y += 2;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      continue;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    const text = block.text || '';
    if (!text.trim()) {
      y += 3;
      continue;
    }
    y = drawRuns(pdf, block.runs?.length ? block.runs : [{ text }], margin, y, pageWidth, 11, 5, ensureSpace);
    y += 2;
  }

  const blob = pdf.output('blob');
  triggerBrowserDownload(blob, `${safeBase}.pdf`);
}

/**
 * @param {import('@tiptap/core').JSONContent} node
 * @param {object} mod docx module namespace
 */
function mapInlineToRuns(node, mod) {
  const { TextRun, ExternalHyperlink, UnderlineType } = mod;
  if (!node) return [];
  if (node.type === 'hardBreak') {
    return [new TextRun({ break: 1 })];
  }
  if (node.type !== 'text') return [];

  const marks = node.marks || [];
  const bold = marks.some((m) => m.type === 'bold');
  const italics = marks.some((m) => m.type === 'italic');
  const strike = marks.some((m) => m.type === 'strike');
  const underlineMark = marks.some((m) => m.type === 'underline');
  const code = marks.some((m) => m.type === 'code');
  const link = marks.find((m) => m.type === 'link');
  const text = node.text ?? '';

  const runOpts = {
    text,
    bold,
    italics,
    strike,
    font: code ? 'Courier New' : undefined,
    shading: code ? { fill: 'F2F2F2' } : undefined,
    underline: underlineMark ? { type: UnderlineType.SINGLE } : undefined,
  };

  if (link?.attrs?.href) {
    return [
      new ExternalHyperlink({
        children: [
          new TextRun({
            ...runOpts,
            style: 'Hyperlink',
          }),
        ],
        link: String(link.attrs.href),
      }),
    ];
  }

  return [new TextRun(runOpts)];
}

/**
 * @param {import('@tiptap/core').JSONContent[]|undefined} content
 * @param {object} mod
 */
function mapInlineContent(content, mod) {
  if (!content?.length) return [new mod.TextRun({ text: '' })];
  return content.flatMap((n) => mapInlineToRuns(n, mod));
}

/**
 * @param {import('@tiptap/core').JSONContent} node
 * @param {object} mod
 * @returns {import('docx').Paragraph[] | import('docx').Table | null}
 */
function mapBlockToDocx(node, mod) {
  const { Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, BorderStyle } = mod;

  if (!node) return null;

  switch (node.type) {
    case 'paragraph':
      return new Paragraph({ children: mapInlineContent(node.content, mod) });
    case 'heading': {
      const level = Math.min(6, Math.max(1, node.attrs?.level ?? 1));
      const hl = HeadingLevel[`HEADING_${level}`];
      return new Paragraph({
        heading: hl,
        children: mapInlineContent(node.content, mod),
      });
    }
    case 'blockquote': {
      /** @type {import('docx').Paragraph[]} */
      const out = [];
      for (const c of node.content || []) {
        if (c.type === 'paragraph') {
          out.push(
            new Paragraph({
              indent: { left: 360 },
              border: {
                left: { style: BorderStyle.SINGLE, size: 12, color: 'BBBBBB' },
              },
              children: mapInlineContent(c.content, mod),
            })
          );
        } else {
          const nested = mapBlockToDocx(c, mod);
          if (!nested) continue;
          if (Array.isArray(nested)) out.push(...nested);
          else out.push(nested);
        }
      }
      return out.length ? out : null;
    }
    case 'codeBlock': {
      const text = extractPlainTextFromNode(node);
      return new Paragraph({
        shading: { fill: 'F2F2F2' },
        children: [new TextRun({ text, font: 'Courier New' })],
      });
    }
    case 'horizontalRule':
      return new Paragraph({
        border: {
          bottom: { color: 'auto', space: 1, style: BorderStyle.SINGLE, size: 6 },
        },
        children: [new TextRun({ text: '' })],
      });
    case 'bulletList':
    case 'orderedList': {
      /** @type {import('docx').Paragraph[]} */
      const items = [];
      let idx = 1;
      for (const li of node.content || []) {
        if (li.type !== 'listItem') continue;
        const prefix = node.type === 'orderedList' ? `${idx}. ` : '• ';
        for (const inner of li.content || []) {
          if (inner.type === 'paragraph') {
            items.push(
              new Paragraph({
                children: [
                  new TextRun({ text: prefix, bold: node.type === 'orderedList' }),
                  ...mapInlineContent(inner.content, mod),
                ],
              })
            );
          } else {
            const nested = mapBlockToDocx(inner, mod);
            if (nested) items.push(...(Array.isArray(nested) ? nested : [nested]));
          }
        }
        idx += 1;
      }
      return items.length ? items : null;
    }
    case 'table': {
      const rows = [];
      let colCount = 0;
      for (const row of node.content || []) {
        if (row.type !== 'tableRow') continue;
        const cells = [];
        for (const cell of row.content || []) {
          if (cell.type !== 'tableCell' && cell.type !== 'tableHeader') continue;
          /** @type {import('docx').Paragraph[]} */
          const cellParas = [];
          for (const c of cell.content || []) {
            const m = mapBlockToDocx(c, mod);
            if (!m) continue;
            if (Array.isArray(m))
              cellParas.push(...m.filter((p) => p instanceof mod.Paragraph));
            else if (m instanceof mod.Paragraph) cellParas.push(m);
          }
          const children = cellParas.length ? cellParas : [new Paragraph({ children: [new TextRun('')] })];
          cells.push(new TableCell({ children }));
        }
        if (!colCount && cells.length) colCount = cells.length;
        if (cells.length) rows.push(new TableRow({ children: cells }));
      }
      if (!rows.length) return null;
      const nCols = colCount || 1;
      const colW = Math.floor(9000 / nCols);
      const columnWidths = Array.from({ length: nCols }, () => colW);
      return new Table({ columnWidths, rows });
    }
    default:
      return null;
  }
}

/**
 * @param {import('@tiptap/core').Editor} editor
 * @param {string} fileNameBase
 */
export async function exportRichTextToDocx(editor, fileNameBase) {
  const mod = await import('docx');
  const { Document, Packer, Paragraph, TextRun } = mod;
  const json = editor.getJSON();
  const safeBase = sanitizeDownloadFileBase(fileNameBase);

  /** @type {import('docx').FileChild[]} */
  const children = [];

  if (json?.content) {
    for (const node of json.content) {
      const mapped = mapBlockToDocx(node, mod);
      if (!mapped) continue;
      if (Array.isArray(mapped)) children.push(...mapped);
      else children.push(mapped);
    }
  }

  if (!children.length) {
    children.push(new Paragraph({ children: [new TextRun('')] }));
  }

  const doc = new Document({
    sections: [
      {
        properties: {},
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  triggerBrowserDownload(blob, `${safeBase}.docx`);
}
